import { Fragment } from 'react';
import SharedStepBox, { StepBoxColor } from '../../../shared/shared-step-box';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Close from '@mui/icons-material/Close';

export type HelpStepColor = StepBoxColor;

const TITLE_COLOR_MAP: Record<
  HelpStepColor,
  'success' | 'darkBlue' | 'warning'
> = {
  green: 'success',
  blue: 'darkBlue',
  amber: 'warning',
};

export interface HelpStepBoxProps {
  title: string;
  subtitle: string;
  body: string;
  example: string;
  color: HelpStepColor;
  sx?: SxProps<Theme>;
}

export function HelpStepBox({
  title,
  subtitle,
  body,
  example,
  color,
  sx,
}: HelpStepBoxProps) {
  const theme = useTheme();

  return (
    <SharedStepBox
      color={color}
      sx={{
        flex: 1,
        width: { xs: '100%', md: 'auto' },
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '143%',
            letterSpacing: '0.17px',
            color: theme.palette.semantic.text[TITLE_COLOR_MAP[color]],
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 800,
            lineHeight: 1.2,
            color: theme.palette.colors.neutral[800],
          }}
        >
          {subtitle}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            lineHeight: 1.2,
            color: theme.palette.colors.neutral[800],
          }}
        >
          {body}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          gap: '8px',
          alignSelf: 'stretch',
          borderRadius: '4px',
          backgroundColor: theme.palette.common.white,
          boxSizing: 'border-box',
          minHeight: '28px',
          mt: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.colors.neutral[600],
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}
        >
          {example}
        </Typography>
      </Box>
    </SharedStepBox>
  );
}

const HELP_STEPS: HelpStepBoxProps[] = [
  {
    title: 'WHEN',
    subtitle: 'The trigger',
    body: 'Choose what event starts this rule — like a user being created or a file being shared.',
    example: 'e.g. Admin Event: User Creation in the promevo.com domain',
    color: 'green',
  },
  {
    title: 'IF',
    subtitle: 'The filter (optional)',
    body: 'Narrow when the rule fires. Skip this to run the rule for every matching trigger.',
    example: 'e.g. Only if the user\'s department is "Finance"',
    color: 'blue',
  },
  {
    title: 'THEN',
    subtitle: 'The action',
    body: 'What gPanel does automatically when the rule fires. You can add multiple actions.',
    example: 'e.g. Add user to group, send an email notification',
    color: 'amber',
  },
];

export interface RulesEngineHelpProps {
  onClose?: () => void;
}

export default function RulesEngineHelp({ onClose }: RulesEngineHelpProps) {
  const theme = useTheme();

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: '8px',
        boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.12)',
      }}
    >
      <CardHeader
        action={
          onClose ? (
            <IconButton size="small" aria-label="close help" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          ) : undefined
        }
        title="How rules work"
        subheader="Rules let you automate actions in Google Workspace — define a trigger, add optional filters, and choose what gPanel does when conditions are met."
        slotProps={{
          title: {
            variant: 'h6',
            sx: { fontWeight: 500 },
          },
          subheader: {
            variant: 'body2',
            color: 'theme.palette.semantic.text.support',
          },
        }}
        sx={{
          borderBottom: `1px solid ${theme.palette.semantic.outline.light}`,
        }}
      />
      <CardContent sx={{ padding: { xs: 2, sm: '24px 36px' } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'stretch' }}
            justifyContent="space-between"
            sx={{ width: '100%', gap: 1 }}
          >
            {HELP_STEPS.map((step, index) => (
              <Fragment key={step.title}>
                {index > 0 && (
                  <ArrowForward
                    sx={{
                      color: theme.palette.semantic.icon.default,
                      flexShrink: 0,
                      alignSelf: 'center',
                    }}
                  />
                )}
                <HelpStepBox
                  title={step.title}
                  subtitle={step.subtitle}
                  body={step.body}
                  example={step.example}
                  color={step.color}
                />
              </Fragment>
            ))}
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: 'theme.palette.semantic.text.support',
            }}
          >
            *Rules run automatically. You can disable or edit them at any time.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
